import { useState } from 'react';
import { Card, Space, Typography, Tag, Timeline, Radio, Select, Tooltip } from 'antd';
import moment from 'moment';

import BloomAccountUpdateModal from './BloomAccountUpdateModal';
import BloomAccountStatus, { StatusItem } from './BloomAccountStatus';
import PatchStatementsModal from '../StatementPatch/PatchStatementsModal';
import StatementFurnishment from '../StatementPatch/StatementView';
import AccountDeleteModal from '../AccountDeleteModal';
import ActionsMenu from '../../../ActionsMenu';
import { indexOf } from 'lodash';
import { isSupportManager, toWriteOff } from '../../../../helpers/genericHelper';
import Data from '../../../CustomText/data';

const { Text } = Typography;

// Anything over this is a reported late payment, shown in one color however late it is
const LATE_PAYMENT_DAYS_LIMIT = 29;
const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

const isLate = (statement) => Number(statement?.daysDelinquent) > LATE_PAYMENT_DAYS_LIMIT;

const ordinal = (day) => {
  const n = Number(day);
  if (!n) return day;
  const suffix = (n % 100 >= 11 && n % 100 <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
  return `${n}${suffix}`;
};

const formatStatus = (status) => status
  ? status.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())
  : 'Unknown';

const monthState = (days) => Number(days) > LATE_PAYMENT_DAYS_LIMIT ? 'late' : 'ok';

// Read in UTC, so a timestamp at midnight UTC stays in its own month in US time zones.
// Missing or unreadable dates give null instead of an invalid date.
const toUtcDate = (date) => {
  if (!date) return null;
  const parsed = moment.utc(date);
  return parsed.isValid() ? parsed : null;
};

// One row per year, newest on top, built from the statements already reported
function PaymentHistoryGrid({ statements, openDate, closureDate }) {
  const byMonth = {};
  statements.forEach((statement) => {
    const date = toUtcDate(statement.statementDate);
    if (!date) return;
    const key = date.format('YYYY-MM');
    // A month can have more than one statement (e.g. patched); show the worst
    if (!byMonth[key] || Number(statement.daysDelinquent) > Number(byMonth[key].daysDelinquent)) {
      byMonth[key] = statement;
    }
  });

  const reportedMonths = Object.keys(byMonth).sort();
  if (reportedMonths.length === 0) return null;

  const firstReported = moment.utc(reportedMonths[0], 'YYYY-MM');
  const lastReported = moment.utc(reportedMonths[reportedMonths.length - 1], 'YYYY-MM');
  const opened = toUtcDate(openDate) || firstReported;
  // The history stops once the account is closed; until then it runs to this month
  const closed = toUtcDate(closureDate);
  const now = moment.utc();
  const end = closed && closed.isBefore(now, 'month') ? closed : now;
  // A bad open date long before the first statement would add rows of empty years, so start at most a year earlier
  const firstYear = Math.max(Math.min(opened.year(), firstReported.year()), firstReported.year() - 1);
  const lastYear = Math.max(end.year(), lastReported.year());
  const years = [];
  for (let year = lastYear; year >= firstYear; year--) years.push(year);

  const lateCount = Object.values(byMonth).filter(isLate).length;

  return (
    <Card size="small" className="furnish-card" title={<>
      Payment History with Ava{' '}
      <Text type="secondary" className="furnish-card-subtitle">
        · {reportedMonths.length} months reported · {reportedMonths.length - lateCount} on time · {lateCount} late
      </Text>
    </>} extra={<Space size={10} className="furnish-legend">
      <span><span className="furnish-month ok" /> On time</span>
      <span><span className="furnish-month late" /> Late (30+ days)</span>
      <span><span className="furnish-month none" /> Not reported</span>
    </Space>}>
      <div className="furnish-grid-row furnish-grid-head">
        <span />
        {MONTHS.map((month, index) => <span key={index}>{month}</span>)}
      </div>
      {years.map((year) => (
        <div className="furnish-grid-row" key={year}>
          <Text strong>{year}</Text>
          {MONTHS.map((_, index) => {
            const month = moment.utc({ year, month: index, day: 1 });
            const key = month.format('YYYY-MM');
            const statement = byMonth[key];
            if (statement) {
              return (
                <Tooltip key={key} title={<>
                  <div><b>{month.format('MMM YYYY')}</b>: {isLate(statement) ? `${statement.daysDelinquent} days late` : 'on time'}</div>
                  <div>Balance: {statement.currentBalance ?? '-'}</div>
                  <div>Paid: {statement.actualMonthlyPayment ?? '-'}</div>
                  <div>Past due: {statement.amountPastDue ?? '-'}</div>
                </>}>
                  <span className={`furnish-month ${monthState(statement.daysDelinquent)}`} />
                </Tooltip>
              );
            }
            // Nothing is drawn after the account was closed
            if (closed && month.isAfter(closed, 'month')) return <span key={key} />;
            const beforeOpen = month.isBefore(opened, 'month');
            const future = month.isAfter(end, 'month');
            return <span key={key} className={`furnish-month ${beforeOpen || future ? 'empty' : 'none'}`} title={beforeOpen ? 'Before the account was opened' : undefined} />;
          })}
        </div>
      ))}
      <Text type="secondary" className="furnish-grid-note">
        One row per year, newest on top. Dashed boxes are before the account opened or still to come. Hover a month for its numbers.
        {closed && !closed.isAfter(now, 'month') && <> The account was closed in {closed.format('MMM YYYY')}, so the history stops there.</>}
      </Text>
    </Card>
  );
}

export default function AccountDetails({ fullName, account, isCC = false, refresh, userId, creditCards }) {
  const [statementFilter, setStatementFilter] = useState('all');
  const [statementYear, setStatementYear] = useState('all');

  const shouldShowDeleteAccount = (accountStatus) => {
    return indexOf(['DELETED_CONFIRMED_FRAUD', 'DELETED'], accountStatus) === -1;
  }

  const accountStatements = account?.statements || [];
  const statementTime = (statement) => toUtcDate(statement.statementDate)?.valueOf() || 0;
  const lateStatements = accountStatements
    .filter(isLate)
    .sort((a, b) => statementTime(b) - statementTime(a));
  const statementYears = [...new Set(accountStatements
    .map((statement) => toUtcDate(statement.statementDate)?.year())
    .filter(Boolean))]
    .sort((a, b) => b - a);

  const shownStatements = accountStatements
    .filter((statement) => statementFilter === 'all' || isLate(statement))
    .filter((statement) => statementYear === 'all' || toUtcDate(statement.statementDate)?.year() === statementYear)
    .sort((a, b) => (Number(b.reportingCycle) - Number(a.reportingCycle)) || (statementTime(b) - statementTime(a)));

  const statementType = isCC ? 'creditCard' : 'savingsBuilder';

  // Grouped by reporting cycle, newest cycle first
  const statementsByCycle = shownStatements.reduce((acc, statement) => {
    (acc[statement.reportingCycle] = acc[statement.reportingCycle] || []).push(statement);
    return acc;
  }, {});
  const reportingCycles = Object.keys(statementsByCycle).sort((a, b) => Number(b) - Number(a));

  const latePaymentValue = accountStatements.length === 0
    ? 'No statements yet'
    : lateStatements.length === 0
      ? 'No'
      : 'Yes';
  const lateMonths = lateStatements.slice(0, 3).map((statement) => toUtcDate(statement.statementDate)?.format('MMM YYYY') || 'Unknown month');

  const actionGroups = [
    {
      title: 'Account',
      items: [
        isSupportManager() && <BloomAccountUpdateModal key="update" furnishingAccount={account} refresh={refresh} isCC={isCC}/>,
        isSupportManager() && shouldShowDeleteAccount(account?.accountStatus) && <PatchStatementsModal
          key="patch"
          statements={account?.statements}
          accountId={account?.accountId}
          productId={account?.productId}
          userId={userId}
          accountType="Credit Card"
          userName={fullName}
          refresh={refresh} />
      ]
    },
    {
      title: 'Danger Zone',
      danger: true,
      items: [
        isSupportManager() && shouldShowDeleteAccount(account?.accountStatus) &&
          <AccountDeleteModal key="delete" refresh={refresh} furnishingAccount={account} creditCard={isCC} creditCardTransactions={isCC ? creditCards : []} />
      ]
    }
  ];

  return (
    <Space direction="vertical" size={14} style={{ width: '100%' }}>
      {/* Account */}
      <Card size="small" className="furnish-card furnish-account">
        <div className="furnish-account-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: 16 }}>{isCC ? 'Credit Card' : 'Save & Build'} Account</Text>
            <div>
              <Text type="secondary">
                What Ava reports to the credit bureaus (through Bloom) for this account
                {account?.furnishmentDay ? <>, every month on the <Text strong>{ordinal(account.furnishmentDay)}</Text></> : null}.
              </Text>
            </div>
          </div>
          <ActionsMenu groups={actionGroups} />
        </div>
        <div className="furnish-fields">
          <Data title="Opened" value={account?.openDate} />
          <Data title="Closed" value={account?.closureDate} emptyValue="—" />
          <Data title="Statement Day" value={account?.furnishmentDay ? `${ordinal(account.furnishmentDay)} of each month` : null} />
          <Data title="Bloom Account ID" value={account?.accountId} copyable />
          <Data title="Ava Product ID" value={account?.productId} copyable />
          <Data title="Tradeline Account #" value={account?.accountNumber} copyable />
          {!isCC && <Data title="SB Type" value={account?.productType} />}
        </div>
        <div className="furnish-status-strip">
          <StatusItem
            label="Status"
            ok={account?.accountStatus === "CURRENT"}
            value={formatStatus(isCC ? account?.accountStatus : toWriteOff(account?.accountStatus))} />
          <StatusItem
            label="Late payment reported"
            ok={lateStatements.length === 0}
            value={latePaymentValue}
            details={lateMonths.length > 0
              ? `${lateMonths.join(' · ')}${lateStatements.length > 3 ? ` +${lateStatements.length - 3} more` : ''}`
              : null} />
          <BloomAccountStatus productId={account?.productId} reloadData={refresh} isCC={isCC}/>
        </div>
      </Card>

      {/* Payment history */}
      <PaymentHistoryGrid statements={accountStatements} openDate={account?.openDate} closureDate={account?.closureDate} />

      {/* Statements */}
      <Card
        size="small"
        className="furnish-card"
        title={<>Statements <Text type="secondary" className="furnish-card-subtitle">{accountStatements.length}</Text></>}
        extra={accountStatements.length > 0 && <Space size={8} wrap>
          <Radio.Group size="small" value={statementFilter} onChange={(e) => setStatementFilter(e.target.value)}>
            <Radio.Button value="all">All ({accountStatements.length})</Radio.Button>
            <Radio.Button value="late">Late only ({lateStatements.length})</Radio.Button>
          </Radio.Group>
          {statementYears.length > 1 && <Select
            size="small"
            value={statementYear}
            onChange={setStatementYear}
            style={{ minWidth: 110 }}
            options={[{ value: 'all', label: 'All years' }, ...statementYears.map((year) => ({ value: year, label: String(year) }))]} />}
        </Space>}>
        <Timeline mode="left" style={{ paddingTop: 12 }}>
          {reportingCycles.map((reportingCycle) => (
            <Timeline.Item key={reportingCycle} color={statementsByCycle[reportingCycle].some(isLate) ? 'red' : 'green'}>
              <Space direction='vertical' style={{ width: "100%" }}>
                <Text type="secondary" strong>Reporting Cycle: {reportingCycle}</Text>
                {statementsByCycle[reportingCycle].map((statement, j) => (
                  // Late statements are highlighted so they stand out in a long history
                  <div key={j} className={isLate(statement) ? 'furnish-statement late' : 'furnish-statement'}>
                    {isLate(statement) &&
                      <Tag color="error" className="furnish-statement-flag">
                        {statement.daysDelinquent} days late
                      </Tag>}
                    <StatementFurnishment
                      statement={statement}
                      shouldShowDelete={shouldShowDeleteAccount(account?.accountStatus)}
                      type={statementType}
                      allowDownload={true}
                      />
                  </div>
                ))}
              </Space>
            </Timeline.Item>
          ))}
        </Timeline>
        {reportingCycles.length === 0 &&
          <Text strong type="secondary">{statementFilter === 'late' ? 'No late statements' : 'No statements found'}</Text>}
      </Card>
    </Space>
  )
}
